import { useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export const Contact = () => {
  const { t } = useLang();
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", org: "", email: "", role: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 border-b border-[#0B1F3A]/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-12 gap-6 lg:gap-12">
        <div className="col-span-12 lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#D4AF37]" />
            <span className="overline">{t.contact.eyebrow}</span>
          </div>
          <h2 className="font-display text-3xl lg:text-4xl font-light text-[#0B1F3A] leading-tight">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-base lg:text-lg text-[#0B1F3A]/70 leading-relaxed">{t.contact.sub}</p>

          <div className="mt-10 border-t border-[#0B1F3A]/15 pt-6 space-y-3 text-sm text-[#0B1F3A]/80">
            <div className="flex justify-between">
              <span className="text-[#0B1F3A]/50 uppercase tracking-widest text-[10px] font-semibold">Entity</span>
              <span>PT Max Solution Indonesia</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#0B1F3A]/50 uppercase tracking-widest text-[10px] font-semibold">Brand</span>
              <span>MaxSolution™</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#0B1F3A]/50 uppercase tracking-widest text-[10px] font-semibold">Location</span>
              <span>Jakarta · Indonesia</span>
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7">
          {sent ? (
            <div
              data-testid="contact-success"
              className="border border-[#0B1F3A]/15 bg-[#F8F9FA] p-10 flex flex-col items-center text-center"
            >
              <CheckCircle2 size={36} strokeWidth={1.2} className="text-[#D4AF37]" />
              <h3 className="mt-6 font-display text-xl text-[#0B1F3A] font-medium">{t.contact.sent}</h3>
              <button
                onClick={() => { setSent(false); setForm({ name: "", org: "", email: "", role: "", message: "" }); }}
                data-testid="contact-reset"
                className="mt-6 text-xs uppercase tracking-widest text-[#0B1F3A]/60 hover:text-[#0B1F3A] font-semibold border-b border-[#0B1F3A]/30 pb-0.5"
              >
                ← New request
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="border border-[#0B1F3A]/15 bg-white p-6 lg:p-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#0B1F3A]/15 border border-[#0B1F3A]/15">
                <Field testid="form-name" label={t.contact.name} value={form.name} onChange={update("name")} required />
                <Field testid="form-org" label={t.contact.org} value={form.org} onChange={update("org")} required />
                <Field testid="form-email" label={t.contact.email} value={form.email} onChange={update("email")} type="email" required />
                <Field testid="form-role" label={t.contact.role} value={form.role} onChange={update("role")} />
              </div>

              <div className="mt-px border border-[#0B1F3A]/15 bg-white">
                <label className="block px-4 pt-3 text-[10px] uppercase tracking-[0.2em] text-[#0B1F3A]/50 font-semibold">
                  {t.contact.message}
                </label>
                <textarea
                  data-testid="form-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  className="w-full px-4 pb-3 pt-1 text-sm text-[#0B1F3A] outline-none resize-none focus:bg-[#F8F9FA]"
                  required
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-4 bg-[#0B1F3A] text-white text-sm font-semibold tracking-wide hover:bg-[#0B1F3A]/90 transition-colors border border-[#0B1F3A] group"
              >
                {t.contact.submit}
                <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, value, onChange, type = "text", required, testid }) => (
  <div className="bg-white">
    <label className="block px-4 pt-3 text-[10px] uppercase tracking-[0.2em] text-[#0B1F3A]/50 font-semibold">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full px-4 pb-3 pt-1 text-sm text-[#0B1F3A] outline-none focus:bg-[#F8F9FA]"
    />
  </div>
);
